// src/email/components/ComposeModal.jsx
import React, { useState, useEffect, useRef } from 'react';
import {
  fetchDraftId,
  useCreateDraft,
  useDiscardDraft,
  useSendDraft,
  useSendMail,
  useUpdateDraft,
} from '../api/hooks';
import { toast } from 'react-toastify';
import { getCurrentCrmUser } from '../api/client';

const TOAST_OPTIONS = {
  position: 'bottom-left',
  autoClose: 3000,
  closeOnClick: true,
  theme: 'colored',
};

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const res = reader.result;
      const base64 = typeof res === 'string' ? res.split(',')[1] || '' : '';
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function splitAddrs(str) {
  if (Array.isArray(str)) return str.filter(Boolean);
  if (!str || typeof str !== 'string') return [];
  return str
    .split(/[,;\n]/)
    .map((s) => s.trim())
    .filter(Boolean);
}

// "Name <email>" -> "Name"; plain addresses are shown as-is
function chipLabel(addr) {
  const m = /^\s*"?([^"<]*?)"?\s*<[^>]+>\s*$/.exec(addr || '');
  return (m && m[1]) || addr;
}

// Attachment already stored on a saved draft (no content to upload again)
function savedAttachment(a) {
  return {
    id: `saved-${a.id}`,
    savedId: a.id,
    name: a.filename,
    size: a.sizeBytes || 0,
    mimeType: a.mimeType,
  };
}

// Comparable state of the composer, used to tell whether there is anything new to save
function snapshot(to, cc, bcc, subject, body, attachments) {
  return JSON.stringify([to, cc, bcc, subject, body, attachments.map((a) => a.id)]);
}

function prefixed(orig, prefix) {
  if (!orig) return prefix;
  return orig.toLowerCase().startsWith(prefix.toLowerCase()) ? orig : `${prefix} ${orig}`;
}

const AUTOSAVE_DELAY_MS = 2000;

const COMMON_EMOJIS = [
  '😊', '👍', '🙏', '❤️', '🎉', '💼', '🚀', '🔥', 
  '😀', '😂', '🤝', '👏', '✅', '⭐', '💡', '📌', 
  '📈', '📊', '👌', '🙌', '✨', '☕', '💪', '🎯'
];


export function ComposeModal({
  account,
  accounts = [],
  mode = 'new',
  sourceMessage = null,
  onClose,
  onSent,
  onDiscarded,
}) {
  const send = useSendMail();
  const createDraft = useCreateDraft();
  const updateDraft = useUpdateDraft();
  const sendDraft = useSendDraft();
  const discardDraft = useDiscardDraft();
  const isDraft = mode === 'draft';
  // A draft always belongs to the mailbox it was written in
  const draftAccountId = isDraft ? sourceMessage?.emailAccountId || account?.id : null;
  const [draftId, setDraftId] = useState(null);
  const [isDraftLoading, setIsDraftLoading] = useState(false);
  const baselineRef = useRef(null);
  // Saved draft behind this composer: { id, accountId }. Set when a draft is opened or first auto-saved
  const draftRef = useRef(null);
  const savePromiseRef = useRef(null);
  const failedSnapshotRef = useRef(null);
  // Held while sending / closing / discarding so auto-save cannot run alongside
  const autosaveLockRef = useRef(false);
  const [saveStatus, setSaveStatus] = useState(''); // '' | 'saving' | 'saved' | 'error'
  const [isClosingSave, setIsClosingSave] = useState(false);
  const crmUser = getCurrentCrmUser();

  const [isMinimized, setIsMinimized] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);

  // Recipient states
  const [toChips, setToChips] = useState([]);
  const [toInput, setToInput] = useState('');
  const [ccChips, setCcChips] = useState([]);
  const [ccInput, setCcInput] = useState('');
  const [bccChips, setBccChips] = useState([]);
  const [bccInput, setBccInput] = useState('');
  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);
  const [selectedAccountId, setSelectedAccountId] = useState(account?.id || '');

  // Subject & Body
  const [subject, setSubject] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [isReadingFiles, setIsReadingFiles] = useState(false);

  // Toolbars & popovers
  const [showFormatting, setShowFormatting] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showScheduleMenu, setShowScheduleMenu] = useState(false);
  const [aiCustomPrompt, setAiCustomPrompt] = useState('');
  const [isGeneratingAi, setIsGeneratingAi] = useState(false);

  const editorRef = useRef(null);
  const fileInputRef = useRef(null);
  const imageInputRef = useRef(null);
  const toInputRef = useRef(null);

  // Update selectedAccountId if account prop changes
  useEffect(() => {
    if (account?.id) {
      setSelectedAccountId(account.id);
    }
  }, [account?.id]);

  // Initial population when replying, forwarding or reopening a saved draft
  useEffect(() => {
    const init = { to: [], cc: [], bcc: [], subject: '', body: '', attachments: [] };

    if (mode === 'draft' && sourceMessage) {
      init.to = sourceMessage.toAddresses || [];
      init.cc = sourceMessage.ccAddresses || [];
      init.bcc = sourceMessage.bccAddresses || [];
      init.subject = sourceMessage.subject || '';
      init.body =
        sourceMessage.bodyHtml ||
        (sourceMessage.bodyText || '')
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/\n/g, '<br/>');
      // Already-saved files stay on the server and are kept by id on save / send
      init.attachments = (sourceMessage.attachments || []).map(savedAttachment);
    } else if (mode !== 'new' && sourceMessage) {
      const sender = sourceMessage.fromAddress || '';
      init.body = `<br/><br/><div class="gmail-quote-header" style="color: #666; font-size: 13px; border-left: 2px solid #ccc; padding-left: 10px; margin-top: 16px;">
      On ${sourceMessage.internalDate ? new Date(sourceMessage.internalDate).toLocaleString() : ''}, <b>${sender}</b> wrote:<br/>
      <div style="margin-top: 6px; color: #444;">${sourceMessage.bodyHtml || sourceMessage.bodyText || ''}</div>
    </div>`;

      if (mode === 'reply') {
        init.to = sender ? [sender] : [];
        init.subject = prefixed(sourceMessage.subject, 'Re:');
      } else if (mode === 'reply_all') {
        const allTo = sourceMessage.toAddresses || [];
        const others = [sender, ...allTo].filter(
          (addr) => !addr?.toLowerCase().includes((account?.emailAddress || '').toLowerCase())
        );
        init.to = [...new Set(others)];
        init.cc = sourceMessage.ccAddresses || [];
        init.subject = prefixed(sourceMessage.subject, 'Re:');
      } else if (mode === 'forward') {
        init.subject = prefixed(sourceMessage.subject, 'Fwd:');
      }
    }

    setToChips(init.to);
    setCcChips(init.cc);
    setBccChips(init.bcc);
    if (init.cc.length > 0) setShowCc(true);
    if (init.bcc.length > 0) setShowBcc(true);
    setSubject(init.subject);
    setBodyHtml(init.body);
    setAttachments(init.attachments);
    if (editorRef.current) {
      editorRef.current.innerHTML = init.body;
    }
    baselineRef.current = snapshot(
      init.to,
      init.cc,
      init.bcc,
      init.subject,
      init.body,
      init.attachments
    );
  }, [mode, sourceMessage, account?.emailAddress]);

  // A saved draft is updated / sent / discarded by its Gmail draftId
  useEffect(() => {
    setDraftId(null);
    draftRef.current = null;
    if (mode !== 'draft' || !sourceMessage?.id || !draftAccountId) return undefined;

    let cancelled = false;
    setIsDraftLoading(true);
    fetchDraftId(draftAccountId, sourceMessage.id)
      .then((id) => {
        if (cancelled) return;
        draftRef.current = { id, accountId: draftAccountId };
        setDraftId(id);
        setIsDraftLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Failed to load draft:', err);
        toast.error(
          err.response?.status === 404
            ? 'This draft no longer exists. It may have been sent or deleted.'
            : 'Could not open this draft. Please try again.',
          TOAST_OPTIONS
        );
        onClose();
      });

    return () => {
      cancelled = true;
      setIsDraftLoading(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, sourceMessage?.id, draftAccountId]);

  // Handle chips for To / Cc / Bcc
  const handleKeyDownEmail = (e, inputVal, setInputVal, chips, setChips) => {
    if (e.key === 'Enter' || e.key === ',' || e.key === 'Tab') {
      e.preventDefault();
      const trimmed = inputVal.trim().replace(/,$/, '');
      if (trimmed) {
        if (!chips.includes(trimmed)) {
          setChips([...chips, trimmed]);
        }
        setInputVal('');
      }
    } else if (e.key === 'Backspace' && !inputVal && chips.length > 0) {
      setChips(chips.slice(0, -1));
    }
  };

  const handlePasteEmail = (e, chips, setChips, setInputVal) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text');
    if (!pasted) return;
    const addrs = splitAddrs(pasted);
    if (addrs.length > 0) {
      const unique = Array.from(new Set([...chips, ...addrs]));
      setChips(unique);
      setInputVal('');
    }
  };

  const removeChip = (index, chips, setChips) => {
    setChips(chips.filter((_, i) => i !== index));
  };

  // Editor changes & slash command detection
  const handleEditorInput = (e) => {
    const html = editorRef.current ? editorRef.current.innerHTML : '';
    setBodyHtml(html);
  };


  // Formatting helpers using document.execCommand
  const applyFormat = (command, value = null) => {
    if (editorRef.current) {
      editorRef.current.focus();
    }
    document.execCommand(command, false, value);
    if (editorRef.current) {
      setBodyHtml(editorRef.current.innerHTML);
    }
  };

  // Insert Link
  const handleInsertLink = () => {
    const url = prompt('Enter the URL (e.g. https://example.com):', 'https://');
    if (url && url !== 'https://') {
      applyFormat('createLink', url);
    }
  };

  // Insert Emoji
  const handleInsertEmoji = (emoji) => {
    if (editorRef.current) {
      editorRef.current.focus();
      document.execCommand('insertText', false, emoji);
      setBodyHtml(editorRef.current.innerHTML);
    }
    setShowEmojiPicker(false);
  };

  // Apply AI template or custom generated draft
  const handleApplyTemplate = (templateHtml) => {
    if (editorRef.current) {
      const current = editorRef.current.innerHTML;
      const combined = current && current !== '<br>' ? `${current}<br/>${templateHtml}` : templateHtml;
      editorRef.current.innerHTML = combined;
      setBodyHtml(combined);
      editorRef.current.focus();
    }
  };

  const handleGenerateCustomAi = () => {
    if (!aiCustomPrompt.trim()) return;
    setIsGeneratingAi(true);
    setTimeout(() => {
      const generated = `<p>Hi,</p><p>Regarding your request on <strong>${aiCustomPrompt.trim()}</strong>:</p><p>We have reviewed the details and prepared our recommendations for your review. Please let us know if you would like us to arrange a detailed walkthrough call or assist you further.</p><p>Warm regards,</p>`;
      handleApplyTemplate(generated);
      setIsGeneratingAi(false);
      setAiCustomPrompt('');
    }, 600);
  };

  // File Attachments
  async function handleFileSelect(e) {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setIsReadingFiles(true);
    try {
      const added = [];
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        if (file.size > 20 * 1024 * 1024) {
          alert(`"${file.name}" exceeds the 20MB limit.`);
          continue;
        }
        const base64 = await fileToBase64(file);
        added.push({
          id: `${Date.now()}-${i}-${Math.random().toString(36).slice(2, 7)}`,
          name: file.name,
          size: file.size,
          mimeType: file.type || 'application/octet-stream',
          contentBase64: base64,
        });
      }
      setAttachments((prev) => [...prev, ...added]);
    } catch (err) {
      console.error('Failed to read file', err);
      alert('Could not attach file. Please try again.');
    } finally {
      setIsReadingFiles(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }

  // Insert Image into body
  async function handleImageSelect(e) {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    const file = files[0];
    if (file.size > 10 * 1024 * 1024) {
      alert('Image size exceeds 10MB limit.');
      return;
    }
    try {
      const base64 = await fileToBase64(file);
      const imgHtml = `<img src="data:${file.type};base64,${base64}" alt="${file.name}" style="max-width: 100%; height: auto; border-radius: 4px; margin: 8px 0;" />`;
      if (editorRef.current) {
        editorRef.current.focus();
        document.execCommand('insertHTML', false, imgHtml);
        setBodyHtml(editorRef.current.innerHTML);
      }
    } catch (err) {
      console.error('Failed to insert image', err);
    } finally {
      if (imageInputRef.current) imageInputRef.current.value = '';
    }
  }

  function removeAttachment(id) {
    setAttachments((prev) => prev.filter((a) => a.id !== id));
  }

  // Latest composer values, readable from async save / send code without stale closures
  const latestRef = useRef({});
  latestRef.current = {
    toChips,
    toInput,
    ccChips,
    ccInput,
    bccChips,
    bccInput,
    subject,
    bodyHtml,
    attachments,
  };

  // Recipients; the final save / send also takes any address still being typed
  function collectRecipients(includePending) {
    const v = latestRef.current;
    const list = (chips, input) =>
      includePending && input.trim()
        ? Array.from(new Set([...chips, ...splitAddrs(input)]))
        : [...chips];
    return {
      to: list(v.toChips, v.toInput),
      cc: list(v.ccChips, v.ccInput),
      bcc: list(v.bccChips, v.bccInput),
    };
  }

  const currentBody = () =>
    latestRef.current.bodyHtml || (editorRef.current ? editorRef.current.innerHTML : '');

  // Files picked in this session are uploaded; files already on the draft are kept by id
  const newAttachments = () =>
    latestRef.current.attachments
      .filter((a) => !a.savedId)
      .map((a) => ({
        filename: a.name,
        mimeType: a.mimeType,
        contentBase64: a.contentBase64,
      }));

  // Draft update is a full replace, so every field goes each time
  function draftPayload(recipients) {
    const v = latestRef.current;
    return {
      ...recipients,
      subject: v.subject,
      bodyHtml: currentBody(),
      attachments: newAttachments(),
      keepAttachmentIds: v.attachments.filter((a) => a.savedId).map((a) => a.savedId),
    };
  }

  function currentSnapshot(recipients) {
    const v = latestRef.current;
    return snapshot(recipients.to, recipients.cc, recipients.bcc, v.subject, v.bodyHtml, v.attachments);
  }

  function showError(err, fallback) {
    const message = err.response?.data?.message;
    toast.error(
      (Array.isArray(message) ? message.join(', ') : message) || err.message || fallback,
      TOAST_OPTIONS
    );
  }

  const isSending = send.isPending || sendDraft.isPending;
  const isBusy = isSending || discardDraft.isPending || isDraftLoading || isReadingFiles;

  // Creates the draft on the first save and updates it afterwards; saves never overlap.
  // Resolves to false when there was nothing new to save.
  async function saveDraft(includePending = false) {
    if (savePromiseRef.current) await savePromiseRef.current.catch(() => {});
    if (isDraft && !draftRef.current) return false;

    const recipients = collectRecipients(includePending);
    if (currentSnapshot(recipients) === baselineRef.current) return false;

    const sentAttachments = latestRef.current.attachments;
    const sentBody = latestRef.current.bodyHtml;
    const payload = draftPayload(recipients);

    const run = (async () => {
      let data;
      if (draftRef.current) {
        data = await updateDraft.mutateAsync({
          accountId: draftRef.current.accountId,
          draftId: draftRef.current.id,
          payload,
        });
      } else {
        const accountId = selectedAccountId || account?.id;
        const { keepAttachmentIds, ...fields } = payload;
        data = await createDraft.mutateAsync({
          accountId,
          payload: {
            mode,
            ...(mode !== 'new' && sourceMessage?.id ? { sourceMessageId: sourceMessage.id } : {}),
            ...fields,
          },
        });
        draftRef.current = { id: data.draftId, accountId };
        setDraftId(data.draftId);
      }

      // Every file that was sent now lives on the draft and is referenced by its saved id
      const saved = (data?.message?.attachments || []).map(savedAttachment);
      baselineRef.current = snapshot(
        recipients.to,
        recipients.cc,
        recipients.bcc,
        payload.subject,
        sentBody,
        saved
      );

      // Carry over files added or removed while the save was in flight
      const now = latestRef.current.attachments;
      const removedNames = sentAttachments.filter((a) => !now.includes(a)).map((a) => a.name);
      const kept = saved.filter((a) => {
        const i = removedNames.indexOf(a.name);
        if (i < 0) return true;
        removedNames.splice(i, 1);
        return false;
      });
      const merged = [...kept, ...now.filter((a) => !sentAttachments.includes(a))];
      latestRef.current = { ...latestRef.current, attachments: merged };
      setAttachments(merged);
    })();

    savePromiseRef.current = run;
    try {
      await run;
      return true;
    } finally {
      if (savePromiseRef.current === run) savePromiseRef.current = null;
    }
  }

  // Auto-save: a short pause after the last change saves the mail as a draft
  useEffect(() => {
    if (isDraftLoading || isReadingFiles || isSending || discardDraft.isPending) return undefined;

    const timer = setTimeout(async () => {
      if (autosaveLockRef.current) return;
      const snap = currentSnapshot(collectRecipients(false));
      // Unchanged, or the same content already failed to save: wait for the next edit
      if (snap === baselineRef.current || snap === failedSnapshotRef.current) return;

      setSaveStatus('saving');
      try {
        await saveDraft(false);
        failedSnapshotRef.current = null;
        setSaveStatus('saved');
      } catch (err) {
        console.error('Failed to auto-save draft:', err);
        failedSnapshotRef.current = snap;
        setSaveStatus('error');
      }
    }, AUTOSAVE_DELAY_MS);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    toChips,
    ccChips,
    bccChips,
    subject,
    bodyHtml,
    attachments,
    isDraftLoading,
    isReadingFiles,
    isSending,
    discardDraft.isPending,
  ]);

  // Send action
  async function handleSend() {
    if (isBusy || autosaveLockRef.current) return;
    autosaveLockRef.current = true;

    try {
      // Let a running auto-save finish so the mail is sent from its draft, not beside it
      if (savePromiseRef.current) await savePromiseRef.current.catch(() => {});

      const recipients = collectRecipients(true);
      if (recipients.to.length === 0) {
        toast.error('Please specify at least one recipient.', TOAST_OPTIONS);
        if (toInputRef.current) toInputRef.current.focus();
        return;
      }

      const draft = draftRef.current;
      const sendingAccountId = draft?.accountId || selectedAccountId || account?.id;
      if (!sendingAccountId) {
        toast.error('No active email account selected to send from.', TOAST_OPTIONS);
        return;
      }

      if (draft) {
        // Saves the latest edits and sends in the draft's own thread; the draft leaves Drafts
        await sendDraft.mutateAsync({
          accountId: sendingAccountId,
          draftId: draft.id,
          payload: draftPayload(recipients),
        });
      } else {
        await send.mutateAsync({
          emailAccountId: sendingAccountId,
          mode,
          sourceMessageId: sourceMessage?.id,
          ...recipients,
          subject: latestRef.current.subject || '(no subject)',
          bodyHtml: currentBody(),
          attachments: newAttachments(),
        });
      }
      toast.success('Email sent successfully.', TOAST_OPTIONS);
      onClose();
      if (onSent) onSent(sendingAccountId);
    } catch (err) {
      console.error('Failed to send mail:', err);
      showError(err, 'Failed to send email. Please check your connection.');
    } finally {
      autosaveLockRef.current = false;
    }
  }

  // Save & close: keeps whatever was written as a draft
  async function handleSaveAndClose() {
    if (isSending || discardDraft.isPending || autosaveLockRef.current) return;
    if (isDraftLoading || isReadingFiles) {
      onClose();
      return;
    }

    autosaveLockRef.current = true;
    setIsClosingSave(true);
    try {
      const saved = await saveDraft(true);
      if (saved) toast.success('Draft saved.', TOAST_OPTIONS);
      onClose();
    } catch (err) {
      console.error('Failed to save draft:', err);
      showError(err, 'Could not save the draft. Use the bin icon to close without saving.');
      setIsClosingSave(false);
      autosaveLockRef.current = false;
    }
  }

  // Discard: a saved draft is permanently deleted, an unsaved mail is simply dropped
  async function handleDiscard() {
    if (isSending || discardDraft.isPending || autosaveLockRef.current) return;
    autosaveLockRef.current = true;

    // A running auto-save may be creating the draft that has to be deleted
    if (savePromiseRef.current) await savePromiseRef.current.catch(() => {});

    const draft = draftRef.current;
    if (!draft) {
      onClose();
      return;
    }
    if (!window.confirm('Discard this draft? It will be permanently deleted.')) {
      autosaveLockRef.current = false;
      return;
    }

    try {
      await discardDraft.mutateAsync({ accountId: draft.accountId, draftId: draft.id });
    } catch (err) {
      // 404: already sent or deleted elsewhere, nothing left to discard
      if (err.response?.status !== 404) {
        console.error('Failed to discard draft:', err);
        showError(err, 'Could not discard the draft.');
        autosaveLockRef.current = false;
        return;
      }
    }
    toast.info('Draft discarded.', TOAST_OPTIONS);
    onClose();
    if (onDiscarded) onDiscarded();
  }

  const saveStatusText = { saving: 'Saving…', saved: 'Draft saved', error: 'Draft not saved' }[
    saveStatus
  ];

  const windowTitle =
    {
      new: subject ? subject : 'New Message',
      reply: subject ? subject : 'Reply',
      reply_all: subject ? subject : 'Reply All',
      forward: subject ? subject : 'Forward',
      draft: subject ? subject : 'Draft',
    }[mode] || 'New Message';

  const isEditorEmpty = !bodyHtml || bodyHtml === '<br>' || bodyHtml.trim() === '';

  return (
    <>
      {/* Optional backdrop when in maximized/fullscreen mode */}
      {isMaximized && (
        <div
          className="gmail-compose-backdrop"
          onClick={() => setIsMaximized(false)}
        />
      )}

      <div
        className={`gmail-compose-window ${
          isMinimized ? 'is-minimized' : ''
        } ${isMaximized ? 'is-maximized' : ''}`}
      >
        {/* Top Header Bar */}
        <div
          className="gmail-compose-header"
          onClick={() => {
            if (isMinimized) setIsMinimized(false);
          }}
        >
          <div className="gmail-compose-header-title">
            <span>{windowTitle}</span>
          </div>

          <div
            className="gmail-compose-header-controls"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Minimize button */}
            <button
              type="button"
              className="gmail-header-btn"
              onClick={() => setIsMinimized(!isMinimized)}
              title={isMinimized ? 'Expand' : 'Minimize'}
              aria-label="Minimize"
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 13H5v-2h14v2z" />
              </svg>
            </button>

            {/* Expand / Maximize button */}
            <button
              type="button"
              className="gmail-header-btn"
              onClick={() => {
                setIsMinimized(false);
                setIsMaximized(!isMaximized);
              }}
              title={isMaximized ? 'Exit full screen' : 'Full screen'}
              aria-label="Full screen"
            >
              {isMaximized ? (
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="4 14 10 14 10 20" />
                  <polyline points="20 10 14 10 14 4" />
                  <line x1="14" y1="10" x2="21" y2="3" />
                  <line x1="3" y1="21" x2="10" y2="14" />
                </svg>
              ) : (
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M15 3h6v6" />
                  <path d="M9 21H3v-6" />
                  <path d="M21 3l-7 7" />
                  <path d="M3 21l7-7" />
                </svg>
              )}
            </button>

            {/* Close / Save button */}
            <button
              type="button"
              className="gmail-header-btn gmail-header-btn-close"
              onClick={handleSaveAndClose}
              title="Save & close"
              aria-label="Close"
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
              </svg>
            </button>
          </div>
        </div>

        {(isSending || isClosingSave || isDraftLoading) && (
          <div className="gmail-compose-sending-banner">
            <span className="mini-spinner-white" />
            <span>
              {isSending ? 'Sending email message…' : isClosingSave ? 'Saving draft…' : 'Loading draft…'}
            </span>
          </div>
        )}

        {/* Content area: hidden when minimized */}
        {!isMinimized && (
          <div className="gmail-compose-content">
            {/* Account Selector (if multiple accounts available) */}
            {accounts.length > 1 && (
              <div className="gmail-field-row gmail-from-row">
                <span className="gmail-field-label">From</span>
                <select
                  className="gmail-from-select"
                  value={draftRef.current?.accountId || draftAccountId || selectedAccountId}
                  disabled={isDraft || Boolean(draftId)}
                  onChange={(e) => setSelectedAccountId(e.target.value)}
                >
                  {accounts.map((acct) => (
                    <option key={acct.id} value={acct.id}>
                      {acct.emailAddress}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Recipients ("To") Field */}
            <div
              className="gmail-field-row gmail-recipients-row"
              onClick={() => toInputRef.current?.focus()}
            >
              <span className="gmail-field-label">
                {toChips.length > 0 || toInput ? 'To' : 'Recipients'}
              </span>

              <div className="gmail-chips-container">
                {toChips.map((chip, idx) => (
                  <span key={idx} className="gmail-chip">
                    <span className="gmail-chip-text" title={chip}>
                      {chipLabel(chip)}
                    </span>
                    <button
                      type="button"
                      className="gmail-chip-remove"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeChip(idx, toChips, setToChips);
                      }}
                    >
                      ×
                    </button>
                  </span>
                ))}
                <input
                  ref={toInputRef}
                  type="text"
                  className="gmail-field-input"
                  placeholder={toChips.length === 0 ? '' : ''}
                  value={toInput}
                  onChange={(e) => setToInput(e.target.value)}
                  onKeyDown={(e) =>
                    handleKeyDownEmail(e, toInput, setToInput, toChips, setToChips)
                  }
                  onPaste={(e) =>
                    handlePasteEmail(e, toChips, setToChips, setToInput)
                  }
                />
              </div>

              <div className="gmail-field-actions" onClick={(e) => e.stopPropagation()}>
                {!showCc && (
                  <button
                    type="button"
                    className="gmail-btn-field-action"
                    onClick={() => setShowCc(true)}
                  >
                    Cc
                  </button>
                )}
                {!showBcc && (
                  <button
                    type="button"
                    className="gmail-btn-field-action"
                    onClick={() => setShowBcc(true)}
                  >
                    Bcc
                  </button>
                )}
              </div>
            </div>

            {/* CC Field */}
            {showCc && (
              <div className="gmail-field-row">
                <span className="gmail-field-label">Cc</span>
                <div className="gmail-chips-container">
                  {ccChips.map((chip, idx) => (
                    <span key={idx} className="gmail-chip">
                      <span className="gmail-chip-text" title={chip}>
                        {chipLabel(chip)}
                      </span>
                      <button
                        type="button"
                        className="gmail-chip-remove"
                        onClick={() => removeChip(idx, ccChips, setCcChips)}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                  <input
                    type="text"
                    className="gmail-field-input"
                    value={ccInput}
                    onChange={(e) => setCcInput(e.target.value)}
                    onKeyDown={(e) =>
                      handleKeyDownEmail(e, ccInput, setCcInput, ccChips, setCcChips)
                    }
                    onPaste={(e) =>
                      handlePasteEmail(e, ccChips, setCcChips, setCcInput)
                    }
                    autoFocus
                  />
                </div>
              </div>
            )}

            {/* BCC Field */}
            {showBcc && (
              <div className="gmail-field-row">
                <span className="gmail-field-label">Bcc</span>
                <div className="gmail-chips-container">
                  {bccChips.map((chip, idx) => (
                    <span key={idx} className="gmail-chip">
                      <span className="gmail-chip-text" title={chip}>
                        {chipLabel(chip)}
                      </span>
                      <button
                        type="button"
                        className="gmail-chip-remove"
                        onClick={() => removeChip(idx, bccChips, setBccChips)}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                  <input
                    type="text"
                    className="gmail-field-input"
                    value={bccInput}
                    onChange={(e) => setBccInput(e.target.value)}
                    onKeyDown={(e) =>
                      handleKeyDownEmail(e, bccInput, setBccInput, bccChips, setBccChips)
                    }
                    onPaste={(e) =>
                      handlePasteEmail(e, bccChips, setBccChips, setBccInput)
                    }
                    autoFocus
                  />
                </div>
              </div>
            )}

            {/* Subject Field */}
            <div className="gmail-field-row gmail-subject-row">
              <input
                type="text"
                className="gmail-field-input gmail-subject-input"
                placeholder="Subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
              />
            </div>

            {/* Body / Message Area */}
            <div
              className="gmail-body-wrapper"
              onClick={() => editorRef.current?.focus()}
            >

              <div
                ref={editorRef}
                className="gmail-editor-content"
                contentEditable
                suppressContentEditableWarning
                onInput={handleEditorInput}
              />
            </div>

            {/* Attachments Section */}
            {isReadingFiles && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 12px', background: '#eff6ff', borderRadius: '6px', margin: '4px 16px', color: '#1d4ed8', fontSize: '12px', fontWeight: 500 }}>
                <span className="mini-spinner" />
                <span>Reading attachment file(s)…</span>
              </div>
            )}
            {attachments.length > 0 && (
              <div className="gmail-attachments-bar">
                {attachments.map((att) => (
                  <div key={att.id} className="gmail-attachment-pill">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
                    </svg>
                    <span className="gmail-attachment-name" title={att.name}>
                      {att.name}
                    </span>
                    <span className="gmail-attachment-size">
                      ({Math.round(att.size / 1024)} KB)
                    </span>
                    <button
                      type="button"
                      className="gmail-attachment-remove"
                      onClick={() => removeAttachment(att.id)}
                      title={`Remove ${att.name}`}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Inline Formatting Toolbar (toggled by Aa button) */}
            {showFormatting && (
              <div className="gmail-formatting-bar">
                <button
                  type="button"
                  className="gmail-fmt-btn"
                  onClick={() => applyFormat('bold')}
                  title="Bold (Ctrl+B)"
                >
                  <strong>B</strong>
                </button>
                <button
                  type="button"
                  className="gmail-fmt-btn"
                  onClick={() => applyFormat('italic')}
                  title="Italic (Ctrl+I)"
                >
                  <em>I</em>
                </button>
                <button
                  type="button"
                  className="gmail-fmt-btn"
                  onClick={() => applyFormat('underline')}
                  title="Underline (Ctrl+U)"
                >
                  <u>U</u>
                </button>
                <button
                  type="button"
                  className="gmail-fmt-btn"
                  onClick={() => applyFormat('strikeThrough')}
                  title="Strikethrough"
                >
                  <s>S</s>
                </button>
                <div className="gmail-fmt-divider" />
                <button
                  type="button"
                  className="gmail-fmt-btn"
                  onClick={() => applyFormat('justifyLeft')}
                  title="Align Left"
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M15 15H3v2h12v-2zm0-8H3v2h12V7zM3 13h18v-2H3v2zm0 8h18v-2H3v2zM3 3v2h18V3H3z" />
                  </svg>
                </button>
                <button
                  type="button"
                  className="gmail-fmt-btn"
                  onClick={() => applyFormat('justifyCenter')}
                  title="Align Center"
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M7 15v2h10v-2H7zm-4 6h18v-2H3v2zm0-8h18v-2H3v2zm4-6v2h10V7H7zM3 3v2h18V3H3z" />
                  </svg>
                </button>
                <button
                  type="button"
                  className="gmail-fmt-btn"
                  onClick={() => applyFormat('justifyRight')}
                  title="Align Right"
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M3 21h18v-2H3v2zm6-4h12v-2H9v2zm-6-4h18v-2H3v2zm6-4h12V7H9v2zM3 3v2h18V3H3z" />
                  </svg>
                </button>
                <div className="gmail-fmt-divider" />
                <button
                  type="button"
                  className="gmail-fmt-btn"
                  onClick={() => applyFormat('insertUnorderedList')}
                  title="Bulleted list"
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M4 10.5c-.83 0-1.5.67-1.5 1.5s.67 1.5 1.5 1.5 1.5-.67 1.5-1.5-.67-1.5-1.5-1.5zm0-6c-.83 0-1.5.67-1.5 1.5S3.17 7.5 4 7.5 5.5 6.83 5.5 6 4.83 4.5 4 4.5zm0 12c-.83 0-1.5.68-1.5 1.5s.68 1.5 1.5 1.5 1.5-.68 1.5-1.5-.67-1.5-1.5-1.5zM7 19h14v-2H7v2zm0-6h14v-2H7v2zm0-8v2h14V5H7z" />
                  </svg>
                </button>
                <button
                  type="button"
                  className="gmail-fmt-btn"
                  onClick={() => applyFormat('insertOrderedList')}
                  title="Numbered list"
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M2 17h2v.5H3v1h1v.5H2v1h3v-4H2v1zm1-9h1V4H2v1h1v3zm-1 3h1.8L2 13.1v.9h3v-1H3.2L5 10.9V10H2v1zm5-6v2h14V5H7zm0 14h14v-2H7v2zm0-6h14v-2H7v2z" />
                  </svg>
                </button>
                <button
                  type="button"
                  className="gmail-fmt-btn"
                  onClick={() => applyFormat('removeFormat')}
                  title="Clear formatting"
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M3.27 5L2 6.27l6.97 6.97L6.5 19h3l1.57-3.66L16.73 21 18 19.73 3.27 5zM6 5v.18L8.82 8h2.4l-.72 1.68 2.1 2.1L14.21 8H20V5H6z" />
                  </svg>
                </button>
              </div>
            )}

            {/* Bottom Action Footer */}
            <div className="gmail-compose-footer">
              {/* Left Actions */}
              <div className="gmail-footer-left">
                {/* Send Button Split Pill */}
                <div className="gmail-send-split-btn">
                  <button
                    type="button"
                    className="gmail-send-btn-main"
                    onClick={handleSend}
                    disabled={isBusy}
                    title="Send (Ctrl+Enter)"
                  >
                    {isSending ? (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                        <span className="mini-spinner-white" />
                        <span>Sending…</span>
                      </span>
                    ) : (
                      <span>Send</span>
                    )}
                  </button>

                  <div className="gmail-send-btn-divider" />

                  <button
                    type="button"
                    className="gmail-send-btn-arrow"
                    onClick={() => setShowScheduleMenu(!showScheduleMenu)}
                    title="More send options"
                    disabled={isSending}
                  >
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M7 10l5 5 5-5z" />
                    </svg>
                  </button>

                  {/* Schedule send dropdown menu */}
                  {showScheduleMenu && (
                    <div
                      className="gmail-dropdown-popover"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <button
                        type="button"
                        className="gmail-dropdown-item"
                        onClick={() => {
                          setShowScheduleMenu(false);
                          alert('Schedule send option: Message will be queued for tomorrow morning 8:00 AM.');
                        }}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="12" cy="12" r="10" />
                          <polyline points="12 6 12 12 16 14" />
                        </svg>
                        <span>Schedule send</span>
                      </button>
                    </div>
                  )}
                </div>

                {/* Toolbar Icons row (Aa, Pen/Sparkle, Attach, Link, Emoji, Photo, Signature) */}
                <div className="gmail-toolbar-icons">
                  {/* Aa - Formatting */}
                  <button
                    type="button"
                    className={`gmail-icon-btn ${showFormatting ? 'active' : ''}`}
                    onClick={() => setShowFormatting(!showFormatting)}
                    title="Formatting options"
                    aria-label="Formatting options"
                  >
                    <span className="gmail-aa-icon">Aa</span>
                  </button>

                  {/* Paperclip - Attach files */}
                  <button
                    type="button"
                    className="gmail-icon-btn"
                    onClick={() => fileInputRef.current?.click()}
                    title="Attach files"
                    disabled={isReadingFiles}
                    aria-label="Attach files"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                      <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
                    </svg>
                  </button>

                  {/* Link icon - Insert link */}
                  <button
                    type="button"
                    className="gmail-icon-btn"
                    onClick={handleInsertLink}
                    title="Insert link"
                    aria-label="Insert link"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                    </svg>
                  </button>

                  {/* Smiley face - Insert emoji */}
                  <div className="gmail-relative-btn-wrap">
                    <button
                      type="button"
                      className={`gmail-icon-btn ${showEmojiPicker ? 'active' : ''}`}
                      onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                      title="Insert emoji"
                      aria-label="Insert emoji"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                        <circle cx="12" cy="12" r="10" />
                        <path d="M8 14s1.5 2 4 2 4-2 4-2" />
                        <line x1="9" y1="9" x2="9.01" y2="9" />
                        <line x1="15" y1="9" x2="15.01" y2="9" />
                      </svg>
                    </button>

                    {showEmojiPicker && (
                      <div
                        className="gmail-emoji-popover"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="gmail-emoji-grid">
                          {COMMON_EMOJIS.map((emoji, i) => (
                            <button
                              key={i}
                              type="button"
                              className="gmail-emoji-choice"
                              onClick={() => handleInsertEmoji(emoji)}
                            >
                              {emoji}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Photo / Image - Insert photo */}
                  <button
                    type="button"
                    className="gmail-icon-btn"
                    onClick={() => imageInputRef.current?.click()}
                    title="Insert photo"
                    aria-label="Insert photo"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                      <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                      <circle cx="8.5" cy="8.5" r="1.5" />
                      <polyline points="21 15 16 10 5 21" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Right Action: Discard Draft */}
              <div className="gmail-footer-right">
                {saveStatusText && (
                  <span className={`gmail-draft-status ${saveStatus === 'error' ? 'is-error' : ''}`}>
                    {saveStatusText}
                  </span>
                )}
                <button
                  type="button"
                  className="gmail-icon-btn gmail-btn-discard"
                  onClick={handleDiscard}
                  disabled={discardDraft.isPending}
                  title="Discard draft"
                  aria-label="Discard draft"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                    <polyline points="3 6 5 6 21 6" />
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Hidden File Inputs */}
            <input
              type="file"
              ref={fileInputRef}
              multiple
              style={{ display: 'none' }}
              onChange={handleFileSelect}
            />
            <input
              type="file"
              ref={imageInputRef}
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handleImageSelect}
            />
          </div>
        )}
      </div>
    </>
  );
}
